import type { SimulationState, SaveData, SaveSlotMeta, PlayerSettings } from '../core/types';
import type { RNGState } from '../core/rng/SeededRandom';

const SAVE_VERSION = '1';
const DB_NAME = 'office-tycoon-saves';
const STORE_NAME = 'saves';
const AUTOSAVE_SLOT = 0;
const MAX_SLOTS = 4; // 0=autosave, 1-3=manual

export const DEFAULT_SETTINGS: PlayerSettings = {
  masterVolume: 80,
  musicVolume: 70,
  sfxVolume: 100,
  reduceMotion: false,
  showTutorialTips: true,
  analyticsOptIn: false,
  tickCatchUpEnabled: true,
};

/**
 * SaveManager handles persisting and loading game state.
 * Uses IndexedDB on web, with an in-memory fallback for testing.
 */
export class SaveManager {
  private storage: SaveStorage;

  constructor(storage?: SaveStorage) {
    this.storage = storage ?? new InMemoryStorage();
  }

  async save(
    slot: number,
    state: SimulationState,
    rngState: RNGState,
    themeId: string,
    settings: PlayerSettings = DEFAULT_SETTINGS,
  ): Promise<void> {
    if (slot < 0 || slot >= MAX_SLOTS) {
      throw new Error(`Invalid save slot: ${slot}. Must be 0-${MAX_SLOTS - 1}.`);
    }

    const saveData: SaveData = {
      version: SAVE_VERSION,
      themeId,
      timestamp: Date.now(),
      rngState,
      core: state,
      settings,
    };

    const serialized = JSON.stringify(saveData);

    try {
      await this.storage.write(`slot_${slot}`, serialized);
    } catch (error) {
      // Retry once
      try {
        await this.storage.write(`slot_${slot}`, serialized);
      } catch {
        throw new Error(`Save failed after retry: ${error}`);
      }
    }
  }

  async load(slot: number): Promise<SaveData | null> {
    if (slot < 0 || slot >= MAX_SLOTS) {
      throw new Error(`Invalid save slot: ${slot}. Must be 0-${MAX_SLOTS - 1}.`);
    }

    const serialized = await this.storage.read(`slot_${slot}`);
    if (!serialized) return null;

    try {
      const data = JSON.parse(serialized) as SaveData;

      // Basic schema validation
      if (!data.version || !data.core || !data.rngState) {
        console.error('Save data missing required fields');
        return null;
      }

      // Version migration (placeholder — no migrations yet)
      if (data.version !== SAVE_VERSION) {
        return this.migrate(data);
      }

      return data;
    } catch {
      console.error(`Failed to parse save in slot ${slot}`);
      return null;
    }
  }

  async autosave(
    state: SimulationState,
    rngState: RNGState,
    themeId: string,
    settings: PlayerSettings = DEFAULT_SETTINGS,
  ): Promise<void> {
    await this.save(AUTOSAVE_SLOT, state, rngState, themeId, settings);
  }

  async listSlots(): Promise<SaveSlotMeta[]> {
    const slots: SaveSlotMeta[] = [];

    for (let i = 0; i < MAX_SLOTS; i++) {
      const serialized = await this.storage.read(`slot_${i}`);
      if (!serialized) {
        slots.push({
          slot: i,
          companyName: '',
          year: 0,
          week: 0,
          funds: 0,
          prestigeLevel: 0,
          timestamp: 0,
          isEmpty: true,
        });
        continue;
      }

      try {
        const data = JSON.parse(serialized) as SaveData;
        slots.push({
          slot: i,
          companyName: data.core.company.name,
          year: data.core.company.currentYear,
          week: data.core.company.currentWeek,
          funds: data.core.company.funds,
          prestigeLevel: data.core.company.prestigeLevel,
          timestamp: data.timestamp,
          isEmpty: false,
        });
      } catch {
        slots.push({
          slot: i,
          companyName: '',
          year: 0,
          week: 0,
          funds: 0,
          prestigeLevel: 0,
          timestamp: 0,
          isEmpty: true,
        });
      }
    }

    return slots;
  }

  async deleteSlot(slot: number): Promise<void> {
    if (slot < 0 || slot >= MAX_SLOTS) {
      throw new Error(`Invalid save slot: ${slot}.`);
    }
    await this.storage.delete(`slot_${slot}`);
  }

  async hasDirtyFlag(): Promise<boolean> {
    const flag = await this.storage.read('dirty_flag');
    return flag !== null;
  }

  async setDirtyFlag(): Promise<void> {
    await this.storage.write('dirty_flag', '1');
  }

  async clearDirtyFlag(): Promise<void> {
    await this.storage.delete('dirty_flag');
  }

  private migrate(data: SaveData): SaveData {
    // Migration pipeline: v1 → v2 → v3 → current
    // Currently only version 1 exists, so no migrations needed
    console.warn(`Save version ${data.version} is not current (${SAVE_VERSION}). No migration available.`);
    return data;
  }
}

// ─── Storage Backends ────────────────────────────────────────

export interface SaveStorage {
  read(key: string): Promise<string | null>;
  write(key: string, value: string): Promise<void>;
  delete(key: string): Promise<void>;
}

/** In-memory storage for testing. */
export class InMemoryStorage implements SaveStorage {
  private data = new Map<string, string>();

  async read(key: string): Promise<string | null> {
    return this.data.get(key) ?? null;
  }

  async write(key: string, value: string): Promise<void> {
    this.data.set(key, value);
  }

  async delete(key: string): Promise<void> {
    this.data.delete(key);
  }
}

/** IndexedDB storage for web. */
export class IndexedDBStorage implements SaveStorage {
  private dbPromise: Promise<IDBDatabase> | null = null;

  private getDB(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, 1);
        request.onupgradeneeded = () => {
          request.result.createObjectStore(STORE_NAME);
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    }
    return this.dbPromise;
  }

  async read(key: string): Promise<string | null> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(key);
      request.onsuccess = () => resolve((request.result as string) ?? null);
      request.onerror = () => reject(request.error);
    });
  }

  async write(key: string, value: string): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.put(value, key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async delete(key: string): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}
